import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Users, Building2, GitBranch, Bot, Inbox, BarChart3, Settings, Sun, Moon, LogOut, Command as CmdIcon, Zap, ClipboardList, Compass, ShieldCheck, ListChecks } from "lucide-react";
import CommandPalette from "@/components/CommandPalette";
import WorkspaceSwitcher from "@/components/WorkspaceSwitcher";
import { api } from "@/lib/api";
import { OnboardingPill } from "@/components/Onboarding";

const nav = [
  { group: "Pipeline", items: [
    { to: "/deals", label: "Deals", icon: GitBranch },
    { to: "/contacts", label: "Contacts", icon: Users },
    { to: "/companies", label: "Companies", icon: Building2 },
    { to: "/tasks", label: "Tasks", icon: ListChecks },
  ] },
  { group: "Autopilot", items: [
    { to: "/discover", label: "Discover", icon: Compass },
    { to: "/agents", label: "Agents", icon: Bot },
    { to: "/inbox", label: "Inbox", icon: Inbox },
    { to: "/forms", label: "Forms", icon: ClipboardList },
  ] },
  { group: "Insights", items: [
    { to: "/deliverability", label: "Deliverability", icon: ShieldCheck },
    { to: "/analytics", label: "Analytics", icon: BarChart3 },
  ] },
];

const CLIENT_NAV = ["/deals", "/contacts", "/companies", "/analytics"];

function hexToHsl(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  if (!m) return null;
  let [r, g, b] = [1, 2, 3].map((i) => parseInt(m[i], 16) / 255);
  const max = Math.max(r, g, b), min = Math.min(r, g, b); let h = 0, s = 0; const l = (max + min) / 2;
  if (max !== min) { const d = max - min; s = l > 0.5 ? d / (2 - max - min) : d / (max + min); h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4; h *= 60; }
  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

const navClass = ({ isActive }) =>
  `group relative flex items-center gap-2.5 px-2.5 py-[7px] text-[13px] rounded-md transition-[background-color,color] duration-150 ${
    isActive ? "bg-primary/10 text-primary font-medium" : "text-muted-foreground hover:bg-foreground/[0.05] hover:text-foreground"
  }`;

const NavItem = ({ to, label, icon: Icon, testId }) => (
  <NavLink to={to} data-testid={testId} className={navClass}>
    {({ isActive }) => (
      <>
        {isActive && <span className="absolute left-0 top-1/2 -translate-y-1/2 h-4 w-[3px] rounded-r bg-primary" />}
        <Icon className={`w-4 h-4 transition-transform duration-200 ${isActive ? "" : "group-hover:translate-x-[1px]"}`} />
        <span>{label}</span>
      </>
    )}
  </NavLink>
);

export default function Shell({ children, title, actions }) {
  const { user, logout } = useAuth();
  const [brand, setBrand] = useState(null);
  React.useEffect(() => {
    const load = () => api.get("/brand").then((r) => { setBrand(r.data); const hsl = hexToHsl(r.data.accent); if (hsl) document.documentElement.style.setProperty("--primary", hsl); }).catch(() => {});
    load(); window.addEventListener("brand-updated", load); return () => window.removeEventListener("brand-updated", load);
  }, []);
  const isClient = user?.role === "client_view";
  const groups = nav.map((g) => ({ ...g, items: isClient ? g.items.filter((n) => CLIENT_NAV.includes(n.to)) : g.items })).filter((g) => g.items.length);
  const { theme, toggle } = useTheme();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const navigate = useNavigate();
  const customBrand = brand?.has_logo || (brand && brand.name !== user?.workspace_name);

  React.useEffect(() => {
    const h = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  return (
    <div className="h-screen flex bg-background text-foreground">
      {/* Sidebar */}
      <aside className="w-[232px] border-r border-border flex flex-col shrink-0 bg-[hsl(var(--sidebar))]">
        <div className="h-14 px-4 flex items-center gap-2.5">
          {brand?.logo_url ? <img src={brand.logo_url} alt="" className="w-7 h-7 rounded-md object-contain" data-testid="brand-logo" /> : <div className="w-7 h-7 bg-primary flex items-center justify-center rounded-md shadow-[0_2px_10px_hsl(var(--primary)/0.45)]"><Zap className="w-4 h-4 text-primary-foreground" fill="currentColor" /></div>}
          <div className="display text-[15px] font-bold tracking-tight truncate" data-testid="brand-wordmark">{customBrand ? brand.name : "Auto-Close"}</div>
          {!customBrand && <div className="text-[10px] mono font-semibold text-primary border border-primary/30 bg-primary/10 px-1.5 py-px rounded">Ai</div>}
        </div>
        <div className="px-3 pb-2">
          <WorkspaceSwitcher user={user} />
        </div>
        <nav className="flex-1 px-3 overflow-y-auto space-y-4 pt-1">
          {groups.map((g) => (
            <div key={g.group}>
              <div className="eyebrow px-2.5 mb-1">{g.group}</div>
              <div className="space-y-0.5">{g.items.map((n) => <NavItem key={n.to} {...n} testId={`nav-${n.label.toLowerCase()}`} />)}</div>
            </div>
          ))}
        </nav>
        {!isClient && <OnboardingPill />}
        <div className="p-3 space-y-0.5">
          <button
            onClick={() => setPaletteOpen(true)}
            data-testid="btn-command-palette"
            className="w-full flex items-center justify-between px-2.5 py-[7px] text-[13px] rounded-md text-muted-foreground hover:bg-foreground/[0.05] hover:text-foreground transition-[background-color,color] duration-150"
          >
            <span className="flex items-center gap-2.5"><CmdIcon className="w-4 h-4" />Command</span>
            <kbd className="mono text-[10px] border border-border bg-card rounded px-1.5 py-px shadow-sm">⌘K</kbd>
          </button>
          {!isClient && <NavItem to="/settings" label="Settings" icon={Settings} testId="nav-settings" />}
        </div>
      </aside>
      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b border-border px-6 flex items-center justify-between shrink-0 sticky top-0 z-20 bg-background/75 backdrop-blur-xl">
          <div className="flex items-center gap-3 min-w-0">
            <h1 className="display text-[17px] font-bold tracking-tight truncate" data-testid="page-title">{title}</h1>
          </div>
          <div className="flex items-center gap-2">
            {actions}
            <Button variant="ghost" size="icon" onClick={toggle} data-testid="btn-theme-toggle" className="w-8 h-8 text-muted-foreground">
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="btn-user-menu" className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-md hover:bg-muted transition-colors duration-150">
                  <Avatar className="w-7 h-7 ring-2 ring-background shadow-sm">
                    <AvatarFallback className="text-[10px] font-semibold bg-primary/15 text-primary">{(user?.name || "?").slice(0, 2).toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <span className="text-[13px] font-medium hidden sm:inline">{user?.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 shadow-[var(--shadow-lift)]">
                <div className="px-2 py-1.5"><div className="text-[13px] font-medium">{user?.name}</div><div className="text-xs text-muted-foreground">{user?.email}</div></div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-settings">
                  <Settings className="w-4 h-4 mr-2" /> Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 overflow-auto"><div className="page-enter min-h-full">{children}</div></main>
      </div>
      <CommandPalette open={paletteOpen} setOpen={setPaletteOpen} />
    </div>
  );
}
